import React, { useEffect } from 'react';
import { SiteHeader } from './SiteHeader';
import { AdjacentCaseStudies } from './AdjacentCaseStudies';
import { ImageCarousel } from './ImageCarousel';
import type { CaseStudyImage, CaseStudySection } from '../data/amexCaseStudy';
type CaseStudyDetailProps = {
  id: string;
  intro: {
    client: string;
    title: string;
    summary: string;
    background: string;
  };
  images: CaseStudyImage[];
  metrics: {
    value: string;
    label: string;
  }[];
  sections: CaseStudySection[];
  /** Replaces the screenshot carousel with custom media when provided. */
  media?: React.ReactNode;
};
export function CaseStudyDetail({
  id,
  intro,
  images,
  metrics,
  sections,
  media
}: CaseStudyDetailProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);
  return <div className="min-h-screen w-full bg-white font-sans text-ink" data-id="element-39" data-mp="2kler38u6ea1g">
      <div className="mx-auto max-w-[1347px] px-5 pt-10 md:px-10" data-id="element-40" data-mp="3i47ql2qflf2o">
        <SiteHeader data-id="element-41" data-mp="3dbmzwa2tni3d" />
      </div>

      <main className="flex flex-col items-center gap-[30px] pb-16 pt-[30px]" data-id="element-42" data-mp="3dabvc81jy6b7">
        <header className="flex max-w-[700px] flex-col items-center gap-[10px] px-5 text-center" data-id="element-43" data-mp="3y5rtnrjfjsv">
          <p className="text-base font-semibold leading-6" data-id="element-44" data-mp="7x674wat44b4">{intro.client}</p>
          <h1 className="text-headline font-bold" data-id="element-45" data-mp="3qcyu92hzyw2e">{intro.title}</h1>
          <p className="text-subhead text-muted" data-id="element-46" data-mp="3qamfey7jd1tk">{intro.summary}</p>
        </header>

        {media ?? <ImageCarousel key={id} images={images} background={intro.background} data-id="element-47" data-mp="3v976o84t8b5v" />}

        <ul aria-label="Results" className={`grid w-full max-w-[920px] grid-cols-1 gap-4 px-5 md:gap-5 md:px-10 ${metrics.length === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-3'}`} data-id="element-48" data-mp="3gcyaxgk4ofy7">
          {metrics.map(m => <li key={m.label} className="flex flex-col rounded-[30px] bg-positive-bg p-5 [container-type:inline-size]" data-id="element-49" data-mp="3cdphydwwwm3p">
              <span className="whitespace-nowrap text-[clamp(22px,21cqw,40px)] font-normal leading-[1.35] tracking-[-0.02em] text-positive" data-id="element-50" data-mp="15mrcxb41fztc">{m.value}</span>
              <span className="text-xl leading-[27px] tracking-[-0.02em]" data-id="element-51" data-mp="1875uxe0ih1mo">{m.label}</span>
            </li>)}
        </ul>

        <article className="flex w-full max-w-[920px] flex-col px-5 pt-6 text-base leading-[1.7] md:px-10" data-id="element-52" data-mp="1449k49h7q9ta">
          {sections.map((section, i) => {
          const labelId = section.label ? `${id}-${section.label.toLowerCase().replace(/\s+/g, '-')}` : undefined;
          const hasText = Boolean(section.paragraphs?.length || section.bullets?.length);
          return <section key={section.label ?? `section-${i}`} aria-labelledby={labelId} aria-label={labelId ? undefined : 'Screens'} className="grid grid-cols-1 gap-2 border-t border-black/10 py-8 first:border-t-0 first:pt-0 md:grid-cols-[180px_1fr] md:gap-10" data-id="element-53" data-mp="1mvaj2xic25cu" data-mpi={i}>
                {section.label && <h2 id={labelId} className="font-semibold leading-[1.7] text-black" data-id="element-54" data-mp="25uobhb0ez818">
                    {section.label}
                  </h2>}
                {hasText && <div className="flex max-w-[640px] flex-col gap-4" data-id="element-55" data-mp="2i4e1ex81f8gv">
                  {section.paragraphs?.map(p => <p key={p} className="text-body" data-id="element-56" data-mp="2n3h5ofg71ans">
                      {p}
                    </p>)}
                  {section.bullets && <ul className="flex list-disc flex-col gap-2 pl-5 text-body marker:text-body" data-id="element-57" data-mp="2w4f0q88nm7u8">
                      {section.bullets.map(b => <li key={b} data-id="element-58" data-mp="31272rv30jac5">{b}</li>)}
                    </ul>}
                </div>}
                {section.figures && section.figures.length > 0 && <div className={`flex flex-col gap-12 md:col-span-2 ${section.label || hasText ? 'mt-6' : ''}`} data-id="element-59" data-mp="3ave7ddpximb6">
                    {section.figures.map(figure => <figure key={figure.src} className="flex flex-col gap-3" data-id="element-60" data-mp="3jrn5mp267evq">
                        <img src={figure.src} alt={figure.alt} loading="lazy" className="block w-full rounded-[20px] border border-black/10 shadow-[0_12px_32px_rgba(0,0,0,0.08)] max-[699px]:rounded-[10px]" data-id="element-61" data-mp="lbnku7adukxm" />
                        <figcaption className="text-sm leading-[1.5] text-muted" data-id="element-62" data-mp="2ecjmh56xhfj">{figure.caption}</figcaption>
                      </figure>)}
                  </div>}
              </section>;
        })}
        </article>

        <div className="flex w-full justify-center pt-10" data-id="element-63" data-mp="bd7pfs4bopib">
          <AdjacentCaseStudies currentId={id} data-id="element-64" data-mp="6f67jaimuw8f" />
        </div>
      </main>

      <div className="mx-auto max-w-[1347px] px-5 pb-10 md:px-10" data-id="element-65" data-mp="1mu7k2ue57gfi">
        <SiteHeader as="footer" data-id="element-66" data-mp="1mwk7hn2o6izy" />
      </div>
    </div>;
}